import { useCallback, useEffect, useRef, useState } from 'react';
import type { ActiveSearch, ChatMessage, ChatStatus } from '../hooks/useChat';
import type { Citation, ComingSoonProduct, ImageRef, ProductCatalogEntry } from '../types/api';
import type { ProgressStep } from '../utils/progress';
import { isKbUrl } from '../utils/support';
import styles from './ChatArea.module.css';
import ContextPanel from './ContextPanel';
import InputBar from './InputBar';
import MessageList from './MessageList';

// Must match the panelCollapse animation duration in ChatArea.module.css
const PANEL_CLOSE_DURATION = 240;

/**
 * The notice for a conversation whose product or docs version is no longer offered
 * to this user, or null when it still is. A session from before versions were stored
 * has no version, and then only its product is checked.
 */
export function pinnedDocsUnavailable(
  products: ProductCatalogEntry[],
  product: string | null,
  version: string | null,
): string | null {
  if (product === null) return null;
  const entry = products.find((p) => p.slug === product);
  if (entry && (version === null || entry.versions.includes(version))) return null;
  const subject =
    entry && version
      ? `The ${entry.display_name} ${version} documentation is`
      : 'The documentation for this conversation is';
  return `${subject} not currently available. You can still read this conversation; start a new one to keep asking.`;
}

interface Props {
  sessionId: string;
  messages: ChatMessage[];
  isLoadingSession?: boolean;
  status: ChatStatus;
  statusMessage: string;
  activeSearches: ActiveSearch[];
  progress: ProgressStep[];
  streamingContent: string;
  streamingCitations: Citation[];
  product: string | null;
  version: string | null;
  error: string | null;
  onSend: (
    query: string,
    product: string | null,
    attachments?: ImageRef[],
    draftSessionId?: string,
  ) => void;
  onStop: () => void;
  isExiting?: boolean;
  attachmentsEnabled?: boolean;
  products: ProductCatalogEntry[];
  comingSoon: ComingSoonProduct[];
  catalogLoading: boolean;
  selectedSlug: string | null;
  onSelectProduct: (slug: string, version?: string) => void;
  selectedVersion: string | null;
  onContextPanelChange?: (open: boolean) => void;
}

export default function ChatArea({
  sessionId,
  messages,
  isLoadingSession = false,
  status,
  statusMessage,
  activeSearches,
  progress,
  streamingContent,
  streamingCitations,
  product,
  version,
  error,
  onSend,
  onStop,
  isExiting = false,
  attachmentsEnabled = true,
  products,
  comingSoon,
  catalogLoading,
  selectedSlug,
  onSelectProduct,
  selectedVersion,
  onContextPanelChange,
}: Props) {
  const isStreaming = status !== 'idle' && status !== 'error';
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [entering, setEntering] = useState(true);
  const isLoading = isLoadingSession && !entering;
  const [selectedCitations, setSelectedCitations] = useState<Citation[] | null>(null);
  const selectedCitationsRef = useRef<Citation[] | null>(null);
  selectedCitationsRef.current = selectedCitations;
  const [isPanelClosing, setIsPanelClosing] = useState(false);
  // The doc currently shown in the panel; also drives the active inline-chip highlight.
  const [activeDocUrl, setActiveDocUrl] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setEntering(false), 500);
    return () => clearTimeout(t);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional fire-on-change — body doesn't read sessionId; it's the trigger to animate the panel out
  useEffect(() => {
    if (!selectedCitationsRef.current) return;
    setIsPanelClosing(true);
    const t = setTimeout(() => {
      setSelectedCitations(null);
      setActiveDocUrl(null);
      setIsPanelClosing(false);
    }, PANEL_CLOSE_DURATION);
    return () => clearTimeout(t);
  }, [sessionId]);

  // When the whole chat exits (new conversation), co-animate the panel out
  useEffect(() => {
    if (isExiting && selectedCitations) {
      setIsPanelClosing(true);
    }
  }, [isExiting, selectedCitations]);

  // The panel is fixed-position furniture's problem too: report it so anything
  // floating over the right edge can get out of the way. Reported as closed while
  // it collapses, so the two animate back together.
  const contextPanelOpen = selectedCitations !== null && !isPanelClosing;
  useEffect(() => {
    onContextPanelChange?.(contextPanelOpen);
    return () => onContextPanelChange?.(false);
  }, [contextPanelOpen, onContextPanelChange]);

  const productLocked = messages.length > 0;
  // Judged against the caller's catalog, which already leaves out what their flags
  // withhold, so this shows before a follow-up is refused rather than after.
  const unavailableNotice =
    productLocked && !catalogLoading ? pinnedDocsUnavailable(products, product, version) : null;

  const handleSend = (
    query: string,
    pickedProduct: string,
    attachments: ImageRef[],
    draftSessionId: string,
  ) => {
    // A locked conversation always has a product; the fallback is the catalog
    // selection rather than a named slug, so an unexpected null cannot send a
    // follow-up to a product the user never chose.
    onSend(
      query,
      productLocked ? (product ?? selectedSlug) : pickedProduct,
      attachments,
      draftSessionId,
    );
  };

  const handleOpenContext = useCallback((citations: Citation[]) => {
    setIsPanelClosing(false);
    setSelectedCitations(citations);
  }, []);

  const handleCloseContext = useCallback(() => {
    setIsPanelClosing(true);
    setActiveDocUrl(null);
    setTimeout(() => {
      setSelectedCitations(null);
      setIsPanelClosing(false);
    }, PANEL_CLOSE_DURATION);
  }, []);

  const handleDocLinkClick = useCallback((url: string, citations: Citation[]) => {
    // Every route into the page viewer passes here, so a Customer Care article that
    // reaches it by any of them still opens in a new tab.
    if (isKbUrl(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    setIsPanelClosing(false);
    setSelectedCitations(citations);
    setActiveDocUrl(url);
  }, []);

  return (
    <div className={styles.chatView}>
      <div className={styles.chatColumn}>
        <div className={styles.scrollArea} ref={scrollAreaRef}>
          <div
            className={`${styles.inner} ${entering && !isExiting ? styles.innerEntering : ''} ${isExiting ? styles.innerExiting : ''} ${isLoading ? styles.innerFading : ''}`}
          >
            <MessageList
              messages={messages}
              sessionId={sessionId}
              status={status}
              statusMessage={statusMessage}
              activeSearches={activeSearches}
              progress={progress}
              streamingContent={streamingContent}
              streamingCitations={streamingCitations}
              error={error}
              onOpenContext={handleOpenContext}
              onDocLinkClick={handleDocLinkClick}
              activeDocUrl={activeDocUrl}
              scrollContainerRef={scrollAreaRef}
              onChoose={(label) => handleSend(label, product ?? selectedSlug ?? '', [], sessionId)}
              onTypeOwn={() => scrollAreaRef.current?.querySelector('textarea')?.focus()}
              choicesDisabled={unavailableNotice !== null}
            />

            {unavailableNotice && (
              <p className={styles.unavailableNotice} role="status">
                {unavailableNotice}
              </p>
            )}
            <InputBar
              sessionId={sessionId || null}
              onSend={handleSend}
              onStop={onStop}
              isStreaming={isStreaming}
              disabled={unavailableNotice !== null}
              placeholder="Ask a follow-up question..."
              scrollContainerRef={scrollAreaRef}
              attachmentsEnabled={attachmentsEnabled}
              products={products}
              comingSoon={comingSoon}
              catalogLoading={catalogLoading}
              selectedSlug={selectedSlug}
              onSelectProduct={onSelectProduct}
              selectedVersion={selectedVersion}
              productLocked={productLocked}
              lockedProduct={product ?? undefined}
              lockedVersion={version ?? undefined}
            />
          </div>
        </div>
      </div>

      {selectedCitations && (
        <div
          className={`${styles.panelWrapper} ${isPanelClosing ? styles.panelWrapperClosing : ''}`}
        >
          <ContextPanel
            citations={selectedCitations}
            onClose={handleCloseContext}
            activeUrl={activeDocUrl}
            onActiveUrlChange={setActiveDocUrl}
          />
        </div>
      )}
    </div>
  );
}
